<section
  class="workflow-message"
  [attr.aria-labelledby]="instanceId + '-heading'"
>
  <div class="workflow-message__header">
    <div>
      <h6 #editorHeading tabindex="-1" [id]="instanceId + '-heading'">
        Message
      </h6>
      <p>One title and body for this event, shared by its recipients.</p>
    </div>
    @if (configured() && !removalPending() && canEdit) {
      <button
        uiButton
        severity="danger"
        [text]="true"
        [outlined]="false"
        type="button"
        class="workflow-message__remove"
        [disabled]="hasPendingChanges()"
        (click)="stageRemoval()"
      >
        Remove message
      </button>
    }
  </div>

  @if (referenceLoading) {
    <p role="status">Loading approved contextual variables…</p>
  }
  @if (unsupportedVersion) {
    <p class="workflow-message__warning" role="status">
      Saved message version {{ message?.version }} is available for inspection.
      This editor cannot change or remove it; use a compatible editor to
      preserve its content.
    </p>
  } @else if (!canAuthor || inspectionMode) {
    <p class="workflow-message__hint">Read-only message configuration.</p>
  }

  @if (!configured()) {
    <div class="workflow-message__empty">
      <p>
        No custom message configured. Add a title and body, then insert approved
        variables for information that changes with each event.
      </p>
      @if (canEdit) {
        <button
          uiButton
          severity="primary"
          [outlined]="false"
          type="button"
          class="workflow-message__primary"
          [disabled]="!compatibleProfiles.length"
          (click)="beginConfigure()"
        >
          Configure message
        </button>
      }
      @if (!referenceLoading && !compatibleProfiles.length) {
        <p class="workflow-message__hint">
          No context profile is available for this workflow subject and event.
          Message authoring needs an approved profile.
        </p>
      }
    </div>
  } @else if (removalPending()) {
    <p class="workflow-message__warning">
      Message removal is pending. Apply to remove it from the local draft, or
      discard to keep the current message.
    </p>
  } @else {
    <div class="workflow-message__layout">
      <div class="workflow-message__compose">
        <div class="workflow-message__field">
          <label [for]="instanceId + '-context'">Message context</label>
          <select
            uiControl
            [id]="instanceId + '-context'"
            [value]="selectedProfileKey"
            [disabled]="!canEdit"
            [attr.aria-describedby]="
              instanceId + '-context-help ' + instanceId + '-context-errors'
            "
            [attr.aria-invalid]="issuesFor('context').length > 0"
            (change)="changeProfile($any($event.target).value)"
          >
            <option value=":0" disabled>Choose contextual information</option>
            @if (contextProfileCode && !selectedProfile) {
              <option [value]="selectedProfileKey" disabled>
                Unavailable: {{ contextProfileCode }} (version
                {{ contextProfileVersion }})
              </option>
            }
            @for (profile of compatibleProfiles; track profileKey(profile)) {
              <option [value]="profileKey(profile)">
                {{ profile.label }} · version {{ profile.version }}
              </option>
            }
          </select>
          <p class="workflow-message__hint" [id]="instanceId + '-context-help'">
            {{
              selectedProfile?.description ||
                "Choose the approved set of data this message can use. Changing it keeps your text; incompatible variables must be corrected."
            }}
          </p>
          <ul
            class="workflow-message__errors"
            [id]="instanceId + '-context-errors'"
          >
            @for (issue of issuesFor("context"); track $index) {
              <li>{{ issue.message }}</li>
            }
          </ul>
        </div>

        <div class="workflow-message__field">
          <div class="workflow-message__field-label">
            <label [for]="instanceId + '-title'"
              >Title <span>(required)</span></label
            ><span>{{ titleTemplate.length }} / 200</span>
          </div>
          <input
            uiControl
            #titleInput
            type="text"
            [id]="instanceId + '-title'"
            [value]="titleTemplate"
            maxlength="200"
            [readOnly]="!canEdit"
            [attr.aria-invalid]="issuesFor('title').length > 0"
            [attr.aria-describedby]="instanceId + '-title-errors'"
            (input)="changeText('title', $any($event.target).value)"
          />
          <ul
            class="workflow-message__errors"
            [id]="instanceId + '-title-errors'"
          >
            @for (issue of issuesFor("title"); track $index) {
              <li>{{ issue.message }}</li>
            }
          </ul>
        </div>

        <div class="workflow-message__field">
          <div class="workflow-message__field-label">
            <label [for]="instanceId + '-body'"
              >Message body <span>(required)</span></label
            ><span>{{ bodyTemplate.length }} / 8,000</span>
          </div>
          <textarea
            uiControl
            #bodyInput
            [id]="instanceId + '-body'"
            rows="7"
            [value]="bodyTemplate"
            maxlength="8000"
            [readOnly]="!canEdit"
            [attr.aria-invalid]="issuesFor('body').length > 0"
            [attr.aria-describedby]="
              instanceId + '-body-help ' + instanceId + '-body-errors'
            "
            (input)="changeText('body', $any($event.target).value)"
          ></textarea>
          <p class="workflow-message__hint" [id]="instanceId + '-body-help'">
            Plain text. Insert variables below; expressions, formatting
            instructions and HTML rendering are not supported.
          </p>
          <ul
            class="workflow-message__errors"
            [id]="instanceId + '-body-errors'"
          >
            @for (issue of issuesFor("body"); track $index) {
              <li>{{ issue.message }}</li>
            }
          </ul>
        </div>

        @if (selectedProfile?.code === "hearing_schedule_change") {
          <p class="workflow-message__context-note">
            Hearing messages need the inmate details and the previous and new
            schedule captured together when the change occurs. The preview uses
            sample data. A current hearing date alone cannot supply the previous
            date.
          </p>
        }

        <details class="workflow-message__variables">
          <summary>
            Insert contextual information
            <span>({{ variables.length }} variables)</span>
          </summary>
          <p class="workflow-message__hint">
            Place the cursor in the title or body, then choose an insertion
            button. Values come from the event context, not from arbitrary
            fields or code.
          </p>
          @if (!variables.length) {
            <p class="workflow-message__hint">
              Choose an available context profile to see its variables.
            </p>
          }
          @for (group of variableGroups; track group.label) {
            <div class="workflow-message__variable-group">
              <h6>{{ group.label }}</h6>
              @for (variable of group.variables; track variable.code) {
                <div class="workflow-message__variable">
                  <div>
                    <strong>{{ variable.label }}</strong
                    ><code [textContent]="'{{' + variable.code + '}}'"></code>
                    <p>{{ variable.description }}</p>
                    <small>Sample: {{ variable.sampleValue }}</small>
                  </div>
                  <div class="workflow-message__insert-actions">
                    <button
                      uiButton
                      type="button"
                      [disabled]="!canEdit"
                      [attr.aria-label]="
                        'Insert ' + variable.label + ' into title'
                      "
                      (click)="insertVariable(variable.code, 'title')"
                    >
                      Into title
                    </button>
                    <button
                      uiButton
                      type="button"
                      [disabled]="!canEdit"
                      [attr.aria-label]="
                        'Insert ' + variable.label + ' into message body'
                      "
                      (click)="insertVariable(variable.code, 'body')"
                    >
                      Into body
                    </button>
                  </div>
                </div>
              }
            </div>
          }
        </details>
      </div>

      <section
        class="workflow-message__preview"
        [attr.aria-labelledby]="instanceId + '-preview-heading'"
      >
        <h6 [id]="instanceId + '-preview-heading'">Sample preview</h6>
        <p class="workflow-message__hint">
          Fictional example data. This preview does not send a notification or
          confirm that live context is connected.
        </p>
        <label [for]="instanceId + '-scenario'">Preview scenario</label>
        <select
          uiControl
          [id]="instanceId + '-scenario'"
          [value]="missingContextScenario() ? 'missing' : 'sample'"
          [disabled]="interactionBlocked"
          (change)="
            missingContextScenario.set($any($event.target).value === 'missing')
          "
        >
          <option value="sample">All sample values available</option>
          <option value="missing" [disabled]="!usedVariableCodes.length">
            A required contextual value is missing
          </option>
        </select>
        @if (preview; as rendered) {
          @if (rendered.errors.length) {
            <div class="workflow-message__withheld" role="status">
              <strong>Preview unavailable</strong>
              <ul>
                @for (issue of rendered.errors; track $index) {
                  <li>{{ issue.message }}</li>
                }
              </ul>
              <p>
                The whole message is withheld when validation fails or required
                context is missing.
              </p>
            </div>
          } @else {
            <div class="workflow-message__sample">
              <strong>{{ rendered.title }}</strong>
              <p>{{ rendered.body }}</p>
            </div>
          }
        }
      </section>
    </div>
  }

  @if (localError()) {
    <p class="workflow-message__warning" role="alert">{{ localError() }}</p>
  }
  @if (hasPendingChanges()) {
    <div class="workflow-message__pending">
      <p>
        Message edits are pending. Apply updates the local draft; Save draft
        persists it.
      </p>
      <div class="workflow-message__actions">
        <button
          uiButton
          severity="primary"
          [outlined]="false"
          type="button"
          class="workflow-message__primary"
          [disabled]="!canEdit"
          (click)="applyPendingChanges()"
        >
          {{ removalPending() ? "Apply removal" : "Apply message" }}</button
        ><button
          uiButton
          type="button"
          [disabled]="interactionBlocked"
          (click)="discardPendingChanges()"
        >
          Discard message changes
        </button>
      </div>
    </div>
  }
  @if (localStatus()) {
    <p class="workflow-message__hint" role="status">{{ localStatus() }}</p>
  }
</section>
